import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Icon } from "./Icon";
import { TaskCard } from "./TaskCard";
import type { KanbanTask } from "./KanbanBoard";
import type { EffortUnit } from "@/lib/effort";

export type KanbanColumnProps = {
  status: "backlog" | "todo" | "doing" | "done";
  label: string;
  color: string;
  tasks: KanbanTask[];
  users: Record<string, { name: string; avatarUrl?: string | null }>;
  isDropTarget: boolean;
  rejectingId: string | null;
  onAddTask?: (status: "backlog" | "todo" | "doing" | "done") => void;
  effortUnit?: EffortUnit;
};

export function KanbanColumn({
  status,
  label,
  color,
  tasks,
  users,
  isDropTarget,
  rejectingId,
  onAddTask,
  effortUnit,
}: KanbanColumnProps) {
  // Register the column container as a droppable target so dropping onto an
  // empty column (no sortable items) still produces a non-null `over` in
  // onDragEnd.
  const { setNodeRef } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className="col"
      role="group"
      aria-label={`${label} column, ${tasks.length} tasks`}
      data-testid={`kanban-column-${status}`}
    >
      <div className="col-head">
        <span className="ch-dot" style={{ background: color }} />
        <span className="ch-name">{label}</span>
        <span className="ch-count">{tasks.length}</span>
        {onAddTask && (
          <button
            type="button"
            className="ch-add"
            aria-label={`Add task in ${label}`}
            onClick={() => onAddTask(status)}
          >
            <Icon name="plus" size={12} />
          </button>
        )}
      </div>
      <div className={`col-list${isDropTarget ? " drop-on" : ""}`}>
        <SortableContext
          items={tasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              users={users}
              rejecting={rejectingId === t.id}
              effortUnit={effortUnit}
            />
          ))}
        </SortableContext>
      </div>
      {onAddTask && (
        <button
          type="button"
          className="add-card"
          onClick={() => onAddTask(status)}
        >
          <Icon name="plus" size={12} /> Add task
        </button>
      )}
    </div>
  );
}
